import React, {useEffect, useMemo, useState, useRef, useCallback} from 'react';
import XLSX from 'xlsx-js-style';
import {CarsTable} from '../../../components/cars';
import {
    ChangeShipperConsigneeModal,
    ChangeDestinationModal,
    ChangeVoyageModal,
    SetFreightCollectModal,
    SetLengthPricingModal,
    Toast,
    KeyboardShortcuts,
    KeyboardShortcut,
    BulkActionsMenu,
    BulkActionKey,
    buildContextMenuGroups,
    Modal,
    PrintBLOptions,
} from '../../../components';
import {BulkLengthPricingPayload, BulkPricingUpdatePayload, Car, ContextMenuGroup, ContextActionKey} from '../../../types';
import {useTableKeyboardShortcuts} from '../../../hooks/useTableKeyboardShortcuts';
import {useToast} from '../../../hooks/useToast';
import {useSearchPreference} from '../../../hooks/useSearchPreference';
import {useFilterPreference} from '../../../hooks/useFilterPreference';
import {useFocusMode, FocusMode} from '../../../hooks/useFocusMode';
import {useCarsModals} from '../../../hooks/useCarsModals';
import {carService, printService} from '../../../services';
import {FilterOption} from '../../../components/tables/DataTable';
import {buildBillsForPrint} from '../../../utils/carUtils';
import {handleDelete} from '../../../utils/deleteUtils';

// Export columns configuration - same as BulkActionsMenu
const EXPORT_COLUMNS = [
    {key: 'bookingRef', label: 'Booking Ref', header: 'BOOKING REF'},
    {key: 'makeModel', label: 'Make/Model', header: 'MAKE/MODEL'},
    {key: 'chassisNumber', label: 'Chassis NR.', header: 'CHASSIS NR.'},
    {key: 'shipper', label: 'Shipper', header: 'SHIPPER'},
    {key: 'consignee', label: 'Consignee', header: 'CONSIGNEE'},
    {key: 'address', label: 'Address', header: 'ADDRESS'},
    {key: 'destination', label: 'Destination', header: 'DESTINATION'},
    {key: 'year', label: 'Year', header: 'YEAR'},
    {key: 'shortChassis', label: 'Short Chassis', header: 'SHORT CHASSIS'},
];

const DIMENSION_COLUMNS = [
    {key: 'length', label: 'Length', header: 'LENGTH'},
    {key: 'width', label: 'Width', header: 'WIDTH'},
    {key: 'height', label: 'Height', header: 'HEIGHT'},
    {key: 'cbm', label: 'CBM', header: 'CBM'},
    {key: 'weight', label: 'Weight', header: 'WEIGHT'},
];

// Address defaults off; every other column defaults on.
const getDefaultColumnSelection = () => Object.fromEntries(EXPORT_COLUMNS.map(col => [col.key, col.key !== 'address']));

interface CarsManagementViewProps {
    headerContent: React.ReactNode;
    cars: Car[];
    loading: boolean;
    showVoyageColumn?: boolean;
    keyboardShortcuts?: KeyboardShortcut[];
    onAddCar?: () => void;
    onBulkAdd?: () => void;
    onEditCar?: (car: Car) => void;
    onDeleteCar?: (car: Car) => void | Promise<void>;
    onRowDoubleClick?: (car: Car) => void;
    onBulkDelete?: (carIds: string[]) => void | Promise<void>;
    onBulkChangeVoyage?: (carIds: string[], voyageId: string) => void | Promise<void>;
    onBulkChangeConsignee?: (carIds: string[], consigneeId: string) => void | Promise<void>;
    onBulkChangeDestination?: (carIds: string[], portId: string) => void | Promise<void>;
    onBulkUpdatePricing?: (carIds: string[], payload: BulkPricingUpdatePayload) => void | Promise<void>;
    onBulkSetLengthPricing?: (carIds: string[], payload: BulkLengthPricingPayload) => void | Promise<void>;
    onBulkSetLoaded?: (carIds: string[], loaded: boolean) => void | Promise<void>;
    onBulkCombineBl?: (carIds: string[]) => void | Promise<void>;
    onBulkSeparateBl?: (carIds: string[]) => void | Promise<void>;
    onBulkAddInsurance?: (carIds: string[]) => void | Promise<void>;
    onBulkRemoveInsurance?: (carIds: string[]) => void | Promise<void>;
    onToggleLoaded?: (car: Car, newValue: boolean) => void | Promise<void>;
    onPrintBookingConfirmation?: (selectedCars: Car[], options: { includeRelated: boolean }) => Promise<void>;
    onRefresh?: () => void | Promise<void>;
    loadingLoadedIds?: string[];
    actionExtras?: React.ReactNode;
    emptyMessage?: string;
    // Controlled sort props for server-side sorting
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
    // Pagination props
    totalCars?: number;
    hasMore?: boolean;
    loadingMore?: boolean;
    onLoadMore?: () => void;
    // Focus mode - voyage ID for backend focus query
    voyageId?: string;
    // Scroll to a specific car when data loads
    scrollToCarId?: string;
    // Backend search callback - when provided, search is done on backend instead of client-side
    onSearch?: (query: string, column: string) => void;
    // Focus mode configuration - 'voyage' uses voyage-specific APIs, 'global' uses global APIs
    focusMode?: FocusMode;
    // Server-side filter options callback
    getFilterOptions?: (columnKey: string, currentFilters?: Record<string, string[]>) => Promise<FilterOption[]>;
    // Callback when filters change (for server-side filtering)
    onFiltersChange?: (filters: Record<string, string[]>) => void;
    // Controlled column filters (passed from parent for server-side filtering)
    columnFilters?: Record<string, string[]>;
    // Table ID for localStorage persistence (focus mode)
    tableId?: string;
}

const defaultEmptyMessage = 'No cars found';

export const CarsManagementView: React.FC<CarsManagementViewProps> = ({
                                                                          headerContent,
                                                                          cars,
                                                                          loading,
                                                                          showVoyageColumn = false,
                                                                          keyboardShortcuts,
                                                                          onAddCar,
                                                                          onBulkAdd,
                                                                          onEditCar,
                                                                          onDeleteCar,
                                                                          onRowDoubleClick,
                                                                          onBulkDelete,
                                                                          onBulkChangeVoyage,
                                                                          onBulkChangeConsignee,
                                                                          onBulkChangeDestination,
                                                                          onBulkUpdatePricing,
                                                                          onBulkSetLengthPricing,
                                                                          onBulkSetLoaded,
                                                                          onBulkCombineBl,
                                                                          onBulkSeparateBl,
                                                                          onBulkAddInsurance,
                                                                          onBulkRemoveInsurance,
                                                                          onToggleLoaded,
                                                                          onPrintBookingConfirmation,
                                                                          onRefresh,
                                                                          loadingLoadedIds,
                                                                          actionExtras,
                                                                          emptyMessage = defaultEmptyMessage,
                                                                          sortColumn,
                                                                          sortDirection,
                                                                          onSort,
                                                                          totalCars,
                                                                          hasMore,
                                                                          loadingMore,
                                                                          onLoadMore,
                                                                          voyageId,
                                                                          scrollToCarId,
                                                                          onSearch,
                                                                          focusMode: focusModeProp,
                                                                          getFilterOptions,
                                                                          onFiltersChange,
                                                                          columnFilters: controlledColumnFilters,
                                                                          tableId,
                                                                      }) => {
    const {toast, showToast, hideToast} = useToast();

    // Resolve tableId - use prop if provided, otherwise compute from voyageId
    const resolvedTableId = tableId ?? (voyageId ? `cars-table-${voyageId}` : 'cars-table-all');

    // Use the focus mode hook - defaults to 'voyage' mode for backward compatibility
    const focus = useFocusMode({
        mode: focusModeProp || 'voyage',
        voyageId,
        cars,
        onError: (message) => showToast(message, 'error'),
        tableId: resolvedTableId,
    });

    // Use the modals hook for centralized modal state management
    const modals = useCarsModals();

    // Define columns for search functionality - matching CarsTable columns
    const columns = useMemo(() => [
        ...(showVoyageColumn ? [{key: 'voyage', label: 'Voyage', searchable: true}] : []),
        {key: 'pod', label: 'POD', searchable: true},
        ...(showVoyageColumn ? [{key: 'ship', label: 'Ship', searchable: true}] : []),
        {key: 'shipper', label: 'Shipper', searchable: true},
        {key: 'booking_ref', label: 'Book.Ref', searchable: true},
        {key: 'consignee', label: 'Consignee', searchable: true},
        {key: 'make_model', label: 'Make/Model', searchable: true},
        {key: 'cat', label: 'Cat', searchable: true},
        {key: 'chs_no', label: 'Chassis', searchable: true},
        {key: 'ref_no', label: 'Ref', searchable: true}
    ], [showVoyageColumn]);

    // Use persisted search preferences
    const {
        searchQuery,
        setSearchQuery,
        searchColumn,
        setSearchColumn,
    } = useSearchPreference({tableId: resolvedTableId, defaultColumn: 'chs_no'});

    // Use persisted filter preferences for column filters (only when not controlled from parent)
    const {filters: internalFilters, setFilters: setInternalFilters, clearFilters: clearInternalFilters} = useFilterPreference({
        tableId: resolvedTableId,
    });

    // Use controlled filters if provided, otherwise use internal state
    const columnFilters = controlledColumnFilters ?? internalFilters;

    // Wrapper to notify parent when filters change (for server-side filtering)
    const setColumnFilters = useCallback((newFilters: Record<string, string[]>) => {
        // Only update internal state if not controlled
        if (!controlledColumnFilters) {
            setInternalFilters(newFilters);
        }
        if (onFiltersChange) {
            onFiltersChange(newFilters);
        }
    }, [controlledColumnFilters, setInternalFilters, onFiltersChange]);

    const clearFilters = useCallback(() => {
        // Only update internal state if not controlled
        if (!controlledColumnFilters) {
            clearInternalFilters();
        }
        if (onFiltersChange) {
            onFiltersChange({});
        }
    }, [controlledColumnFilters, clearInternalFilters, onFiltersChange]);

    // Handle column click - updates active search column
    const handleColumnClick = (columnKey: string) => {
        const column = columns.find(col => col.key === columnKey);
        if (column && column.searchable !== false) {
            setSearchColumn(columnKey);
        }
    };

    // Generate dynamic placeholder text
    const searchPlaceholder = useMemo(() => {
        const activeColumn = columns.find(col => col.key === searchColumn);
        if (activeColumn) {
            return `Search ${activeColumn.label.toLowerCase()}...`;
        }
        return 'Search records...';
    }, [columns, searchColumn]);
    const [selectedCarIds, setSelectedCarIds] = useState<string[]>([]);
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
    const [selectedCarId, setSelectedCarId] = useState<string | undefined>(scrollToCarId);

    // Resolve selected car IDs — fetches all IDs from backend when selectAllAcrossPages is active
    const resolveSelectedCarIds = useCallback(async (): Promise<string[]> => {
        if (selectAllAcrossPages && voyageId) {
            try {
                const activeFilters = controlledColumnFilters || {};
                return await carService.getIdsByVoyage(voyageId, activeFilters);
            } catch (error) {
                console.error('Error fetching all car IDs:', error);
                showToast('Failed to fetch all car IDs. Using visible selection instead.', 'warning');
                return selectedCarIds;
            }
        }
        return selectedCarIds;
    }, [selectAllAcrossPages, voyageId, controlledColumnFilters, selectedCarIds, showToast]);

    // Resolve selected cars as full objects — fetches all from backend when selectAllAcrossPages is active
    const resolveSelectedCars = useCallback(async (): Promise<Car[]> => {
        if (selectAllAcrossPages && voyageId) {
            try {
                const activeFilters = controlledColumnFilters || {};
                const response = await carService.getByVoyage(voyageId, {
                    skip: 0,
                    limit: totalCars || 10000,
                    filters: activeFilters,
                });
                return response.data;
            } catch (error) {
                console.error('Error fetching all cars:', error);
                showToast('Failed to fetch all cars. Using loaded cars instead.', 'warning');
                return cars.filter(car => selectedCarIds.includes(car.id));
            }
        }
        return cars.filter(car => selectedCarIds.includes(car.id));
    }, [selectAllAcrossPages, voyageId, controlledColumnFilters, totalCars, cars, selectedCarIds, showToast]);

    // Reset selectAllAcrossPages when filters or search change
    useEffect(() => {
        setSelectAllAcrossPages(false);
    }, [controlledColumnFilters, searchQuery]);

    // Context menu Export modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportCar, setExportCar] = useState<Car | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>(getDefaultColumnSelection);
    const [includeDimensions, setIncludeDimensions] = useState(false);

    // Debounced backend search
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Handle search input change - call backend search if onSearch is provided
    const handleSearchChange = (newQuery: string) => {
        setSearchQuery(newQuery);

        if (onSearch) {
            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Debounce backend search (300ms)
            debounceTimerRef.current = setTimeout(() => {
                onSearch(newQuery, searchColumn);
            }, 300);
        }
    };

    // Handle search column change - trigger search with new column
    const handleSearchColumnChange = (newColumn: string) => {
        setSearchColumn(newColumn);

        if (onSearch && searchQuery) {
            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Immediately trigger search when column changes (if there's a query)
            debounceTimerRef.current = setTimeout(() => {
                onSearch(searchQuery, newColumn);
            }, 100);
        }
    };

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Trigger backend search on mount if there's a persisted search query
    const initialSearchTriggered = useRef(false);
    useEffect(() => {
        if (!initialSearchTriggered.current && onSearch && searchQuery && searchColumn) {
            initialSearchTriggered.current = true;
            onSearch(searchQuery, searchColumn);
        }
    }, [onSearch, searchQuery, searchColumn]);

    // Notify parent of persisted filters on initial mount (for server-side filtering)
    // Skip if filters are controlled from parent (parent already has the filters)
    const initialFiltersNotified = useRef(false);
    useEffect(() => {
        if (!initialFiltersNotified.current && onFiltersChange && !controlledColumnFilters) {
            const hasFilters = Object.values(columnFilters).some(values => values && values.length > 0);
            if (hasFilters) {
                initialFiltersNotified.current = true;
                onFiltersChange(columnFilters);
            }
        }
    }, [columnFilters, onFiltersChange, controlledColumnFilters]);

    // Sync selectedCarId when scrollToCarId prop changes (e.g., after creating a new car)
    useEffect(() => {
        if (scrollToCarId) {
            setSelectedCarId(scrollToCarId);
        }
    }, [scrollToCarId]);

    // Use cars directly (filtering now handled by CarsTable header filters)
    const filteredCars = cars;

    // Keep selection in sync with available cars when data refreshes
    useEffect(() => {
        setSelectedCarIds(prevSelected => prevSelected.filter(id => filteredCars.some(car => car.id === id)));
    }, [filteredCars]);

    useEffect(() => {
        if (selectedCarId && !filteredCars.some(car => car.id === selectedCarId)) {
            setSelectedCarId(undefined);
        }
    }, [filteredCars, selectedCarId]);

    // Infinite scroll detection
    const loadingMoreRef = useRef(false);
    useEffect(() => {
        loadingMoreRef.current = loadingMore || false;
    }, [loadingMore]);

    useEffect(() => {
        if (!hasMore || !onLoadMore) return;

        let lastScrollTime = 0;
        const throttleMs = 100;

        const handleScroll = () => {
            const now = Date.now();
            if (now - lastScrollTime < throttleMs) return;
            lastScrollTime = now;

            if (loadingMoreRef.current) return;

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            if (scrollTop + windowHeight >= documentHeight - 200) {
                onLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, onLoadMore]);

    const selectedCar = useMemo(() => cars.find(car => car.id === selectedCarId) ?? null, [cars, selectedCarId]);

    // Helper to clear selection and reset across-pages state
    const clearSelection = useCallback(() => {
        setSelectedCarIds([]);
        setSelectAllAcrossPages(false);
    }, []);

    const handleBulkDeleteWithIds = useCallback(async (ids: string[]) => {
        if (!onBulkDelete) {
            showToast('Bulk delete is not available', 'info');
            return;
        }

        const resolvedIds = await resolveSelectedCarIds();
        if (resolvedIds.length === 0) {
            showToast('Select at least one car to continue', 'warning');
            return;
        }

        const deleteCount = selectAllAcrossPages && totalCars ? totalCars : resolvedIds.length;
        await handleDelete(async () => {
            await onBulkDelete(resolvedIds);
            clearSelection();
        }, `You are about to delete ${deleteCount} car${deleteCount > 1 ? 's' : ''}. This cannot be undone.`);
    }, [onBulkDelete, resolveSelectedCarIds, selectAllAcrossPages, totalCars, clearSelection, showToast]);

    // Wrapper for single car delete with confirmation
    const handleDeleteCarWithConfirmation = useCallback(async (car: Car) => {
        if (!onDeleteCar) return;
        await handleDelete(async () => {
            await onDeleteCar(car);
        }, `You are about to delete car "${car.chassis_number}". This cannot be undone.`);
    }, [onDeleteCar]);

    useTableKeyboardShortcuts({
        onDelete: handleDeleteCarWithConfirmation,
        onBulkDelete: handleBulkDeleteWithIds,
        onCreate: onAddCar,
        selectedRow: selectedCar,
        selectedRows: selectedCarIds,
        enabled: !!(onDeleteCar || onAddCar || onBulkDelete),
    });

    const handleBulkAction = useCallback(async (action: BulkActionKey) => {
        if (selectedCarIds.length === 0 && !selectAllAcrossPages) {
            showToast('Select at least one car to continue', 'warning');
            return;
        }

        switch (action) {
            case 'change_voyage':
                if (!onBulkChangeVoyage) {
                    showToast('Change voyage is not available', 'info');
                    return;
                }
                modals.openChangeVoyageModal();
                break;
            case 'change_consignee':
                if (!onBulkChangeConsignee) {
                    showToast('Change consignee is not available', 'info');
                    return;
                }
                modals.openChangeShipperConsigneeModal();
                break;
            case 'change_destination':
                if (!onBulkChangeDestination) {
                    showToast('Change destination is not available', 'info');
                    return;
                }
                modals.openChangeDestinationModal();
                break;
            case 'set_pricing':
                if (!onBulkUpdatePricing) {
                    showToast('Pricing update is not available', 'info');
                    return;
                }
                modals.openSetPricingModal();
                break;
            case 'set_length_pricing':
                if (!onBulkSetLengthPricing) {
                    showToast('Length-based pricing is not available', 'info');
                    return;
                }
                modals.openSetLengthPricingModal();
                break;
            case 'mark_loaded':
                if (onBulkSetLoaded) {
                    try {
                        const ids = await resolveSelectedCarIds();
                        await onBulkSetLoaded(ids, true);
                        clearSelection();
                    } catch (error) {
                        console.error('Error updating loaded status:', error);
                        const message = (error as any)?.response?.data?.detail || 'Failed to update loaded status';
                        showToast(message, 'error');
                    }
                } else {
                    showToast('Load/Unload action is not available', 'info');
                }
                break;
            case 'mark_unloaded':
                if (onBulkSetLoaded) {
                    try {
                        const ids = await resolveSelectedCarIds();
                        await onBulkSetLoaded(ids, false);
                        clearSelection();
                    } catch (error) {
                        console.error('Error updating loaded status:', error);
                        const message = (error as any)?.response?.data?.detail || 'Failed to update loaded status';
                        showToast(message, 'error');
                    }
                } else {
                    showToast('Load/Unload action is not available', 'info');
                }
                break;
            case 'add_insurance':
            case 'remove_insurance': {
                const handler = action === 'add_insurance' ? onBulkAddInsurance : onBulkRemoveInsurance;
                if (!handler) {
                    showToast('Insurance fees are not available', 'info');
                    return;
                }
                try {
                    const ids = await resolveSelectedCarIds();
                    await handler(ids);
                    clearSelection();
                } catch (error) {
                    console.error('Error updating insurance fees:', error);
                    const message = (error as any)?.response?.data?.detail || 'Failed to update insurance fees';
                    showToast(message, 'error');
                }
                break;
            }
            case 'combine_bl':
                if (!onBulkCombineBl) {
                    showToast('Combine BL is not available', 'info');
                    return;
                }
                try {
                    const ids = await resolveSelectedCarIds();
                    if (ids.length < 2) {
                        showToast('Select at least two units to combine', 'warning');
                        return;
                    }
                    await onBulkCombineBl(ids);
                    clearSelection();
                } catch (error) {
                    console.error('Error combining BL:', error);
                    const message = (error as any)?.response?.data?.detail || 'Failed to combine units';
                    showToast(message, 'error');
                }
                break;
            case 'separate_bl':
                if (!onBulkSeparateBl) {
                    showToast('Separate BL is not available', 'info');
                    return;
                }
                try {
                    const ids = await resolveSelectedCarIds();
                    await onBulkSeparateBl(ids);
                    clearSelection();
                } catch (error) {
                    console.error('Error separating BL:', error);
                    const message = (error as any)?.response?.data?.detail || 'Failed to separate units';
                    showToast(message, 'error');
                }
                break;
            case 'delete':
                if (onBulkDelete) {
                    const ids = await resolveSelectedCarIds();
                    const deleteCount = selectAllAcrossPages && totalCars ? totalCars : ids.length;
                    await handleDelete(async () => {
                        await onBulkDelete(ids);
                        clearSelection();
                    }, `You are about to delete ${deleteCount} car${deleteCount > 1 ? 's' : ''}. This cannot be undone.`);
                } else {
                    showToast('Bulk delete is not available', 'info');
                }
                break;
            default:
                break;
        }
    }, [selectedCarIds, selectAllAcrossPages, totalCars, showToast, onBulkChangeVoyage, onBulkChangeConsignee, onBulkChangeDestination, onBulkUpdatePricing, onBulkSetLengthPricing, onBulkSetLoaded, onBulkCombineBl, onBulkSeparateBl, onBulkAddInsurance, onBulkRemoveInsurance, onBulkDelete, modals, resolveSelectedCarIds, clearSelection]);

    const handleChangeVoyageConfirm = async (voyageId: string) => {
        if (!onBulkChangeVoyage) return;

        try {
            const ids = await resolveSelectedCarIds();
            await onBulkChangeVoyage(ids, voyageId);
            modals.closeChangeVoyageModal();
            clearSelection();
        } catch (error) {
            console.error('Error changing voyage:', error);
            const message = (error as any)?.response?.data?.detail || 'Failed to change voyage';
            showToast(message, 'error');
        }
    };

    const handleChangeShipperConsigneeConfirm = async (updates: {
        shipperId?: string;
        consigneeId?: string;
        consigneeName?: string;
        consigneeAddress?: string;
    }) => {
        try {
            const ids = await resolveSelectedCarIds();
            const promises: Promise<any>[] = [];

            // Update shipper if provided
            if (updates.shipperId) {
                promises.push(carService.bulkUpdateShipper(ids, updates.shipperId));
            }

            // Update consignee if provided (either by ID or manual name)
            if (updates.consigneeId || updates.consigneeName) {
                promises.push(carService.bulkUpdateConsignee(
                    ids,
                    updates.consigneeId,
                    updates.consigneeName,
                    updates.consigneeAddress
                ));
            }

            await Promise.all(promises);

            const updatedFields = [];
            if (updates.shipperId) updatedFields.push('shipper');
            if (updates.consigneeId || updates.consigneeName) updatedFields.push('consignee');

            showToast(`Successfully updated ${updatedFields.join(' and ')} for ${ids.length} car(s)`, 'success');
            modals.closeChangeShipperConsigneeModal();
            clearSelection();

            // Refresh data
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error updating shipper/consignee:', error);
            const message = (error as any)?.response?.data?.detail || 'Failed to update shipper/consignee';
            showToast(message, 'error');
        }
    };

    const handleChangeDestinationConfirm = async (portId: string) => {
        if (!onBulkChangeDestination) return;

        try {
            const ids = await resolveSelectedCarIds();
            await onBulkChangeDestination(ids, portId);
            modals.closeChangeDestinationModal();
            clearSelection();
        } catch (error) {
            console.error('Error changing destination:', error);
            const message = (error as any)?.response?.data?.detail || 'Failed to change destination';
            showToast(message, 'error');
        }
    };

    const handleSetPricingConfirm = async (payload: BulkPricingUpdatePayload) => {
        if (!onBulkUpdatePricing) return;

        try {
            const ids = await resolveSelectedCarIds();
            await onBulkUpdatePricing(ids, payload);
            modals.closeSetPricingModal();
            clearSelection();
        } catch (error) {
            console.error('Error updating pricing:', error);
            const message = (error as any)?.response?.data?.detail || 'Failed to update pricing';
            showToast(message, 'error');
        }
    };

    const handleSetLengthPricingConfirm = async (payload: BulkLengthPricingPayload) => {
        if (!onBulkSetLengthPricing) return;

        try {
            const ids = await resolveSelectedCarIds();
            await onBulkSetLengthPricing(ids, payload);
            modals.closeSetLengthPricingModal();
            clearSelection();
        } catch (error) {
            console.error('Error applying length-based pricing:', error);
            const message = (error as any)?.response?.data?.detail || 'Failed to apply length-based pricing';
            showToast(message, 'error');
        }
    };

    const actionGroups = [
        {
            title: 'Change',
            actions: [
                onBulkChangeVoyage && {key: 'change_voyage', label: 'Change Voyage' as const},
                onBulkChangeConsignee && {key: 'change_consignee', label: 'Change Consignee / Shipper' as const},
                onBulkChangeDestination && {key: 'change_destination', label: 'Change Destination' as const},
            ].filter(Boolean) as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Set',
            actions: [
                onBulkSetLoaded && {key: 'mark_loaded', label: 'Mark as Loaded' as const},
                onBulkSetLoaded && {key: 'mark_unloaded', label: 'Mark as Unloaded' as const},
                onBulkUpdatePricing && {key: 'set_pricing', label: 'Set Freight / Collect' as const},
                onBulkSetLengthPricing && {key: 'set_length_pricing', label: 'Set Price Based on Length' as const},
            ].filter(Boolean) as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Insurance',
            actions: [
                onBulkAddInsurance && {key: 'add_insurance', label: 'Add Insurance Fees' as const},
                onBulkRemoveInsurance && {key: 'remove_insurance', label: 'Remove Insurance Fees' as const},
            ].filter(Boolean) as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Bill of Lading',
            actions: [
                onBulkCombineBl && {key: 'combine_bl', label: 'Combine onto one BL' as const},
                onBulkSeparateBl && {key: 'separate_bl', label: 'Separate from BL' as const},
            ].filter(Boolean) as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Print',
            actions: [
                {key: 'print_bill_of_lading', label: 'Print Bill of Lading' as const},
                {key: 'print_booking_confirmation', label: 'Print Booking Confirmation' as const},
            ] as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Export',
            actions: [
                {key: 'export_excel', label: 'Export to Excel' as const},
            ] as { key: BulkActionKey; label: string }[],
        },
        {
            title: 'Danger',
            actions: [
                onBulkDelete && {key: 'delete', label: 'Delete All' as const},
            ].filter(Boolean) as { key: BulkActionKey; label: string }[],
        },
    ];

    // Build context menu groups for right-click functionality
    const buildContextMenuGroupsForCar = useCallback((car: Car): ContextMenuGroup[] => {
        return buildContextMenuGroups(car, {
            onFocus: focus.handleFocus,
            onFocusChassis: focus.handleFocusChassis,
            onFocusShipper: focus.handleFocusShipper,
            onFocusConsignee: focus.handleFocusConsignee,
            onEdit: onEditCar,
            hasChangeVoyage: !!onBulkChangeVoyage,
            hasChangeConsignee: !!onBulkChangeConsignee,
            hasChangeDestination: !!onBulkChangeDestination,
            hasSetLoaded: !!onBulkSetLoaded,
            hasSetPricing: !!onBulkUpdatePricing,
            hasSetLengthPricing: !!onBulkSetLengthPricing,
            hasInsurance: !!onBulkAddInsurance && !!onBulkRemoveInsurance,
            hasPrintBL: true,
            hasPrintBookingConfirmation: !!onPrintBookingConfirmation,
            hasExportExcel: true,
            hasDelete: !!onDeleteCar,
        });
    }, [
        focus.handleFocus, focus.handleFocusChassis, focus.handleFocusShipper, focus.handleFocusConsignee,
        onEditCar, onBulkChangeVoyage, onBulkChangeConsignee, onBulkChangeDestination,
        onBulkSetLoaded, onBulkUpdatePricing, onBulkSetLengthPricing, onBulkAddInsurance, onBulkRemoveInsurance,
        onPrintBookingConfirmation, onDeleteCar
    ]);

    // Helper function to convert car to Bill of Lading data (with optional includeRelated support)
    // Handle Print BL from context menu cascading submenu
    const handlePrintBL = useCallback(async (car: Car, options: PrintBLOptions) => {
        if (!car.voyage) {
            showToast('Car must have a voyage assigned to generate Bill of Lading', 'warning');
            return;
        }
        // Expand to the whole combined group if this unit is on a shared BL.
        const [blData] = buildBillsForPrint([car], cars, { isOriginal: options.isOriginal, includeRelated: options.includeRelated });
        if (!blData) return;

        try {
            const type = options.isOriginal ? 'Original' : 'Copy';
            const filename = `Bill of Lading ${blData.blNumber} ${type}.pdf`;
            await printService.generateBillOfLading(blData, filename);
            showToast('Bill of Lading generated successfully', 'success');
        } catch (error) {
            console.error('Error generating Bill of Lading:', error);
            showToast('Failed to generate Bill of Lading', 'error');
        }
    }, [cars, showToast]);

    // Handle Print Booking Confirmation from context menu cascading submenu
    const handlePrintBookingConfirmation = useCallback(async (car: Car, options: { includeRelated: boolean }) => {
        if (onPrintBookingConfirmation) {
            try {
                await onPrintBookingConfirmation([car], options);
            } catch (error) {
                console.error('Error printing booking confirmation:', error);
                showToast('Failed to print booking confirmation', 'error');
            }
        } else {
            showToast('Print booking confirmation is not available', 'info');
        }
    }, [onPrintBookingConfirmation, showToast]);

    // Handle Export to Excel from context menu with column selection
    const handleExportWithColumns = useCallback(() => {
        if (!exportCar) return;

        // Validate at least one column selected
        const hasSelectedColumn = Object.values(selectedColumns).some(v => v);
        if (!hasSelectedColumn) {
            showToast('Please select at least one column', 'warning');
            return;
        }

        setShowExportModal(false);

        try {
            // Filter columns based on selection and get indices
            const activeColumns = EXPORT_COLUMNS
                .map((col, index) => ({...col, index}))
                .filter(col => selectedColumns[col.key]);
            const activeDimensionColumns = includeDimensions
                ? DIMENSION_COLUMNS.map((col, i) => ({...col, index: EXPORT_COLUMNS.length + i}))
                : [];
            const headers = [
                ...activeColumns.map(col => col.header),
                ...activeDimensionColumns.map(col => col.header),
            ];
            const columnIndices = [
                ...activeColumns.map(col => col.index),
                ...activeDimensionColumns.map(col => col.index),
            ];

            // Track which rows are related units for styling
            const rowIsRelated: boolean[] = [];

            // Build full data first (include related cars)
            const fullData: string[][] = [];
            const car = exportCar;

            const make = car.make?.name || '';
            const model = car.model?.name || '';
            const makeModel = make && model
                ? `${make} ${model}`
                : car.manual_make_model || `${make}${model}`;
            const chassisNumber = car.chassis_number || '';
            const shortChassis = chassisNumber.length >= 6
                ? chassisNumber.slice(-6)
                : chassisNumber;

            // Add main car row (9 base columns + 5 dimension columns) — order must match EXPORT_COLUMNS
            fullData.push([
                (car.booking_reference || '').toUpperCase(),
                makeModel.toUpperCase(),
                chassisNumber.toUpperCase(),
                (car.shipper?.shortname || car.shipper_name || car.shipper?.name || '').toUpperCase(),
                (car.consignee?.name || car.consignee_name || car.name || '').toUpperCase(),
                (car.address || car.consignee?.address || '').toUpperCase(),
                (car.port?.port || car.port_code || '').toUpperCase(),
                String(car.year || ''),
                shortChassis.toUpperCase(),
                car.length != null ? String(car.length) : '',
                car.width != null ? String(car.width) : '',
                car.height != null ? String(car.height) : '',
                car.cbm != null ? String(car.cbm) : '',
                car.weight != null ? String(car.weight) : '',
            ]);
            rowIsRelated.push(false);

            // Add related cars if any (sorted: Attached first, then STC)
            if (car.related_cars && car.related_cars.length > 0) {
                const sortedRelatedCars = [...car.related_cars].sort((a, b) => {
                    if (a.relationship_type === 'Attached' && b.relationship_type === 'STC') return -1;
                    if (a.relationship_type === 'STC' && b.relationship_type === 'Attached') return 1;
                    return 0;
                });
                sortedRelatedCars.forEach(relatedCar => {
                    const relMake = relatedCar.make?.name || '';
                    const relModel = relatedCar.model?.name || '';
                    let relMakeModel = '';
                    if (relMake && relModel) {
                        relMakeModel = `${relMake} ${relModel}`;
                    } else if (relMake || relModel) {
                        relMakeModel = `${relMake}${relModel}`;
                    } else if (relatedCar.manual_make_model) {
                        relMakeModel = relatedCar.manual_make_model;
                    } else {
                        relMakeModel = makeModel;
                    }
                    const relChassisNumber = relatedCar.chassis_number || '';
                    const relShortChassis = relChassisNumber.length >= 6
                        ? relChassisNumber.slice(-6)
                        : relChassisNumber;

                    const prefix = relatedCar.relationship_type === 'STC' ? '└ STC: ' : '└ ATT: ';

                    fullData.push([
                        (car.booking_reference || '').toUpperCase(),
                        (prefix + relMakeModel).toUpperCase(),
                        relChassisNumber.toUpperCase(),
                        (car.shipper?.shortname || car.shipper_name || car.shipper?.name || '').toUpperCase(),
                        (car.consignee?.name || car.consignee_name || car.name || '').toUpperCase(),
                        (car.address || car.consignee?.address || '').toUpperCase(),
                        (car.port?.port || car.port_code || '').toUpperCase(),
                        '',
                        relShortChassis.toUpperCase(),
                        '',
                        '',
                        '',
                        '',
                        '',
                    ]);
                    rowIsRelated.push(true);
                });
            }

            // Filter data to only selected columns
            const data = fullData.map(row => columnIndices.map(i => row[i]));

            // Create worksheet with headers and data
            const worksheetData = [headers, ...data];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Style definitions
            const headerStyle = {
                font: {bold: true, sz: 14},
                fill: {fgColor: {rgb: 'D4DFEF'}},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            const bodyStyle = {
                font: {sz: 11},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            const relatedStyle = {
                font: {sz: 11, italic: true},
                fill: {fgColor: {rgb: 'F0F0F0'}},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            // Calculate auto-fit column widths with improved algorithm
            const colWidths = headers.map((header, colIndex) => {
                let maxLen = header.length;
                data.forEach(row => {
                    const cellValue = String(row[colIndex] || '');
                    if (cellValue.length > maxLen) {
                        maxLen = cellValue.length;
                    }
                });
                // Better width calculation accounting for proportional fonts
                const avgCharWidth = 1.2;
                const padding = 3;
                const calculatedWidth = Math.ceil(maxLen * avgCharWidth) + padding;
                return {wch: Math.min(Math.max(calculatedWidth, 10), 60)};
            });

            // Apply header styles (row 1)
            for (let col = 0; col < headers.length; col++) {
                const cellRef = XLSX.utils.encode_cell({r: 0, c: col});
                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = headerStyle;
                }
            }

            // Apply body styles (rows 2+)
            for (let row = 1; row <= data.length; row++) {
                const isRelated = rowIsRelated[row - 1];
                for (let col = 0; col < headers.length; col++) {
                    const cellRef = XLSX.utils.encode_cell({r: row, c: col});
                    if (worksheet[cellRef]) {
                        worksheet[cellRef].s = isRelated ? relatedStyle : bodyStyle;
                    }
                }
            }

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Car');

            // Set auto-fit column widths
            worksheet['!cols'] = colWidths;

            const filename = `Car_${car.reference_number}_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, filename);

            const totalRows = 1 + (car.related_cars?.length || 0);
            showToast(`Exported ${totalRows} row(s) to Excel`, 'success');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            showToast('Failed to export to Excel', 'error');
        }
    }, [exportCar, selectedColumns, includeDimensions, showToast]);

    // Handle context menu action for single car
    const handleContextAction = useCallback(async (action: ContextActionKey, car: Car) => {
        // Handle focus actions directly
        if (action === 'focus_ref') {
            focus.handleFocus(car);
            return;
        }
        if (action === 'focus_chassis') {
            focus.handleFocusChassis(car);
            return;
        }
        if (action === 'focus_shipper') {
            focus.handleFocusShipper(car);
            return;
        }
        if (action === 'focus_consignee') {
            focus.handleFocusConsignee(car);
            return;
        }

        // Handle edit action directly
        if (action === 'edit') {
            onEditCar?.(car);
            return;
        }

        // Handle delete action directly for single car
        if (action === 'delete') {
            await handleDeleteCarWithConfirmation(car);
            return;
        }

        // For modal-based actions, set the car as the single selection first
        // The modals use selectedCarIds, so we need to update it before opening them
        setSelectedCarIds([car.id]);

        // Handle actions that open modals directly (avoid race condition by not calling handleBulkAction)
        switch (action) {
            case 'change_voyage':
                if (onBulkChangeVoyage) modals.openChangeVoyageModal();
                else showToast('Change voyage is not available', 'info');
                break;
            case 'change_consignee':
                if (onBulkChangeConsignee) modals.openChangeShipperConsigneeModal();
                else showToast('Change consignee is not available', 'info');
                break;
            case 'change_destination':
                if (onBulkChangeDestination) modals.openChangeDestinationModal();
                else showToast('Change destination is not available', 'info');
                break;
            case 'set_pricing':
                if (onBulkUpdatePricing) modals.openSetPricingModal();
                else showToast('Pricing update is not available', 'info');
                break;
            case 'set_length_pricing':
                if (onBulkSetLengthPricing) modals.openSetLengthPricingModal();
                else showToast('Length-based pricing is not available', 'info');
                break;
            case 'add_insurance':
            case 'remove_insurance': {
                const handler = action === 'add_insurance' ? onBulkAddInsurance : onBulkRemoveInsurance;
                if (!handler) {
                    showToast('Insurance fees are not available', 'info');
                    break;
                }
                try {
                    await handler([car.id]);
                } catch (error) {
                    console.error('Error updating insurance fees:', error);
                    showToast('Failed to update insurance fees', 'error');
                }
                break;
            }
            case 'mark_loaded':
                if (onBulkSetLoaded) {
                    try {
                        await onBulkSetLoaded([car.id], true);
                    } catch (error) {
                        console.error('Error setting loaded:', error);
                        showToast('Failed to mark as loaded', 'error');
                    }
                }
                break;
            case 'mark_unloaded':
                if (onBulkSetLoaded) {
                    try {
                        await onBulkSetLoaded([car.id], false);
                    } catch (error) {
                        console.error('Error setting unloaded:', error);
                        showToast('Failed to mark as unloaded', 'error');
                    }
                }
                break;
            // print_bill_of_lading is handled by the ContextMenu's interactive submenu via onPrintBL
            // print_booking_confirmation is handled by the ContextMenu's interactive submenu via onPrintBookingConfirmation
            case 'export_excel':
                // Open Export modal with column selection
                setExportCar(car);
                setSelectedColumns(getDefaultColumnSelection());
                setIncludeDimensions(false);
                setShowExportModal(true);
                break;
            default:
                break;
        }
    }, [
        focus, onEditCar, handleDeleteCarWithConfirmation, showToast, modals,
        onBulkChangeVoyage, onBulkChangeConsignee, onBulkChangeDestination,
        onBulkUpdatePricing, onBulkSetLengthPricing, onBulkSetLoaded, onBulkAddInsurance, onBulkRemoveInsurance
    ]);

    return (
        <div className="space-y-4">
            {headerContent && (
                <div className="px-3 py-4 sm:px-4 lg:px-6">
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                {headerContent}
                            </div>
                            {(keyboardShortcuts?.length || onAddCar || actionExtras) && (
                                <div className="flex items-center space-x-3 flex-shrink-0">
                                    <KeyboardShortcuts shortcuts={keyboardShortcuts}/>
                                    {actionExtras}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="px-3 py-4 sm:px-4 lg:px-6">
                {/* Table with search header */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    {/* Header with search - matching DataTable style */}
                    <div
                        className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                            {/* Left side - Action buttons or Focus indicator */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {focus.isFocused ? (
                                    // Focus mode indicator
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={focus.exitFocusMode}
                                            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                            Exit
                                        </button>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Focus on {focus.focusLabel}
                                        </span>
                                        <span
                                            className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded">
                                            {focus.focusCount} cars
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {onAddCar && (
                                            <button
                                                onClick={onAddCar}
                                                className="bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg transition-all duration-200 px-4 py-2 font-medium text-sm rounded-md inline-flex items-center"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                     viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M12 4v16m8-8H4"/>
                                                </svg>
                                                <span className="hidden sm:inline">Add Car</span>
                                                <span className="sm:hidden">Add</span>
                                            </button>
                                        )}
                                        {onBulkAdd && (
                                            <button
                                                onClick={onBulkAdd}
                                                className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200 px-4 py-2 font-medium text-sm rounded-md inline-flex items-center"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                     viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M12 4v16m8-8H4"/>
                                                </svg>
                                                <span className="hidden sm:inline">Bulk Add</span>
                                                <span className="sm:hidden">Bulk</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Right side - Search and Actions */}
                            <div
                                className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                                <BulkActionsMenu
                                    selectedCarIds={selectedCarIds}
                                    cars={filteredCars}
                                    actionGroups={actionGroups}
                                    onAction={handleBulkAction}
                                    onPrintBookingConfirmation={onPrintBookingConfirmation}
                                    showToast={showToast}
                                    displayCount={selectAllAcrossPages && totalCars ? totalCars : undefined}
                                    resolveSelectedCars={selectAllAcrossPages ? resolveSelectedCars : undefined}
                                />

                                {/* Clear Filters Button - only show when filters are active */}
                                {Object.values(columnFilters).some(values => values && values.length > 0) && (
                                    <button
                                        onClick={clearFilters}
                                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                        </svg>
                                        Clear Filters
                                    </button>
                                )}

                                {/* Search with Column Selector */}
                                <div className="relative flex items-stretch flex-1 max-w-lg">
                                    {/* Column selector dropdown */}
                                    <div className="relative flex-shrink-0">
                                        <select
                                            value={searchColumn}
                                            onChange={(e) => handleSearchColumnChange(e.target.value)}
                                            className="h-full py-2 pl-3 pr-7 text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 border-2 border-r-0 border-gray-200 dark:border-gray-600 rounded-l-xl text-blue-700 dark:text-blue-300 cursor-pointer hover:from-blue-100 hover:to-blue-150 dark:hover:from-blue-800 dark:hover:to-blue-700 focus:outline-none focus:border-blue-500 focus:z-10 transition-all duration-200 appearance-none"
                                            title="Select search column"
                                        >
                                            {columns
                                                .filter(col => col.searchable !== false)
                                                .map(col => (
                                                    <option key={col.key} value={col.key}>
                                                        {col.label}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        {/* Custom dropdown arrow */}
                                        <svg
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-blue-500 dark:text-blue-400 pointer-events-none"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                                                  d="M19 9l-7 7-7-7"/>
                                        </svg>
                                    </div>

                                    {/* Search input */}
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            placeholder={searchPlaceholder}
                                            value={searchQuery}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            className="w-full h-full py-2 pl-9 pr-8 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-2 border-l-0 border-gray-200 dark:border-gray-600 rounded-r-xl placeholder-gray-400 dark:placeholder-gray-500 hover:border-gray-300 dark:hover:border-gray-500 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 transition-all duration-200"
                                        />
                                        <svg
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                        </svg>
                                        {searchQuery && (
                                            <button
                                                onClick={() => handleSearchChange('')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                     viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M6 18L18 6M6 6l12 12"/>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <CarsTable
                        tableId={voyageId ? `cars-table-${voyageId}` : 'cars-table-all'}
                        cars={focus.focusedCars || filteredCars}
                        loading={loading || focus.focusLoading}
                        selectable={true}
                        showVoyageColumn={showVoyageColumn}
                        onRowDoubleClick={onRowDoubleClick}
                        onRowClick={car => setSelectedCarId(car.id)}
                        onSelectionChange={setSelectedCarIds}
                        totalCars={totalCars}
                        selectAllAcrossPages={selectAllAcrossPages}
                        onSelectAllAcrossPages={setSelectAllAcrossPages}
                        selectedRowId={selectedCarId}
                        scrollToRowId={scrollToCarId}
                        onToggleLoaded={onToggleLoaded}
                        loadingLoadedIds={loadingLoadedIds}
                        searchQuery={onSearch ? '' : searchQuery}
                        searchColumn={onSearch ? '' : searchColumn}
                        onColumnClick={handleColumnClick}
                        sortColumn={focus.focusedCars
                            ? (focus.focusedChassis ? 'chs_no' : focus.focusedShipper ? 'shipper' : focus.focusedConsignee ? 'consignee' : 'ref_no')
                            : sortColumn}
                        sortDirection={focus.focusedCars ? 'asc' : sortDirection}
                        onSort={focus.focusedCars ? undefined : onSort}
                        onFocus={focus.handleFocus}
                        onFocusChassis={focus.handleFocusChassis}
                        onFocusShipper={focus.handleFocusShipper}
                        onFocusConsignee={focus.handleFocusConsignee}
                        highlightedRefNo={focus.focusedRefNo}
                        highlightedChassis={focus.focusedChassis}
                        highlightedShipper={focus.focusedShipper}
                        highlightedConsignee={focus.focusedConsignee}
                        columnFilters={columnFilters}
                        onFiltersChange={setColumnFilters}
                        getFilterOptions={getFilterOptions}
                        serverSideFilters={!!getFilterOptions}
                        buildContextMenuGroups={buildContextMenuGroupsForCar}
                        onContextAction={handleContextAction}
                        onPrintBL={handlePrintBL}
                        onPrintBookingConfirmation={onPrintBookingConfirmation ? handlePrintBookingConfirmation : undefined}
                    />

                </div>

                <ChangeVoyageModal
                    isOpen={modals.showChangeVoyageModal}
                    onClose={() => modals.closeChangeVoyageModal()}
                    onConfirm={handleChangeVoyageConfirm}
                    carCount={selectAllAcrossPages && totalCars ? totalCars : selectedCarIds.length}
                />

                <ChangeShipperConsigneeModal
                    isOpen={modals.showChangeShipperConsigneeModal}
                    onClose={() => modals.closeChangeShipperConsigneeModal()}
                    onConfirm={handleChangeShipperConsigneeConfirm}
                    carCount={selectAllAcrossPages && totalCars ? totalCars : selectedCarIds.length}
                />

                <ChangeDestinationModal
                    isOpen={modals.showChangeDestinationModal}
                    onClose={() => modals.closeChangeDestinationModal()}
                    onConfirm={handleChangeDestinationConfirm}
                    carCount={selectAllAcrossPages && totalCars ? totalCars : selectedCarIds.length}
                />

                <SetFreightCollectModal
                    isOpen={modals.showSetPricingModal}
                    onClose={() => modals.closeSetPricingModal()}
                    onConfirm={handleSetPricingConfirm}
                    carCount={selectAllAcrossPages && totalCars ? totalCars : selectedCarIds.length}
                />

                <SetLengthPricingModal
                    isOpen={modals.showSetLengthPricingModal}
                    onClose={() => modals.closeSetLengthPricingModal()}
                    onConfirm={handleSetLengthPricingConfirm}
                    carCount={selectAllAcrossPages && totalCars ? totalCars : selectedCarIds.length}
                />

                {/* Export Column Selection Modal */}
                <Modal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    title="Select Columns to Export"
                    size="sm"
                    footer={
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleExportWithColumns}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                            >
                                Export to Excel
                            </button>
                        </div>
                    }
                >
                    <div className="space-y-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Select the columns you want to include in the Excel export:
                        </p>
                        {EXPORT_COLUMNS.filter(column => column.key !== 'address').map(column => (
                            <label
                                key={column.key}
                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedColumns[column.key]}
                                    onChange={(e) => setSelectedColumns(prev => ({
                                        ...prev,
                                        [column.key]: e.target.checked
                                    }))}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {column.label}
                                </span>
                            </label>
                        ))}
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    checked={includeDimensions}
                                    onChange={(e) => setIncludeDimensions(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Dimensions
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    (Length, Width, Height, CBM, Weight)
                                </span>
                            </label>
                            <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    checked={selectedColumns['address'] ?? false}
                                    onChange={(e) => setSelectedColumns(prev => ({
                                        ...prev,
                                        address: e.target.checked
                                    }))}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Address
                                </span>
                            </label>
                        </div>
                    </div>
                </Modal>

                {!loading && filteredCars.length === 0 && (
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {emptyMessage}
                    </div>
                )}

                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration}
                        onClose={hideToast}
                    />
                )}
            </div>
        </div>
    );
};

export default CarsManagementView;
